// React Dependencies
import React, { useReducer, useEffect, useState } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { Typography, TextField, Button, RadioGroup, FormControlLabel, Radio } from '@material-ui/core';

// Internal Dependencies
import OverLoading from '../../../global/OverLoading';
import apiService from '../../../../services.js/apiService';
import { Status, TrailType } from '../../../../common/constants';

const defaultInputs = { name: '', status: Status.Draft, type: '' };

const Info = ({ match }) => {
  const { pageId, trailId } = match.params;

  const [inputs, setInputs] = useReducer((state, action) => ({ ...state, ...action }), defaultInputs);
  const [isLoading, setIsLoading] = useState(false);
  const [initialStatus, setInitialStatus] = useState(null);

  const isDeactivated = initialStatus === Status.Deactivated;
  const isDraft = initialStatus === Status.Draft;
  const isLive = initialStatus === Status.Live;

  const checkStatusTransitionDisabled = (status) => {
    if (isLive) {
      return status === Status.Draft;
    } else if (isDraft) {
      return status === Status.Deactivated;
    } else if (isDeactivated) {
      return [Status.Live, Status.Draft].includes(status);
    }
    return false;
  };

  const handleChange = (e) => {
    setInputs({ [e.target.name]: e.target.value });
  };

  const getTrail = async () => {
    try {
      setIsLoading(true);
      const { name, status, type } = await apiService.getTrail(pageId, trailId);
      setInputs({ name, status, type });
      setInitialStatus(status);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      await apiService.updateTrail(pageId, trailId, inputs);
      setInitialStatus(inputs.status);
      toast.success('Trail updated successfully.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getTrail();
  }, []);

  return (
    <>
      {isLoading && <OverLoading />}
      <div className="container-fluid">

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <Typography style={{ fontSize: 13 }} className="mb-1">
              Trail Name
            </Typography>
            <div>
              <TextField
                name="name"
                disabled={isDeactivated}
                value={inputs.name}
                variant="outlined"
                onChange={handleChange}
                inputProps={{ style: { fontSize: 13 } }}
                size="small"
                className="w-100"
              />
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-12 col-xl-6 pl-0">
            <Typography style={{ fontSize: 13 }}>
              Type
            </Typography>
            <RadioGroup row name="type" value={inputs.type} onChange={handleChange}>
              {Object.values(TrailType).map((type) => (
                <div key={type} className="mr-3">
                  <FormControlLabel
                    value={type}
                    control={<Radio disabled={isDeactivated} color="secondary" />}
                    style={{ fontSize: 13 }}
                    label={type}
                  />
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-12 col-xl-6 pl-0">
            <Typography style={{ fontSize: 13 }}>
              Status
            </Typography>
            <RadioGroup row name="status" value={inputs.status} onChange={handleChange} defaultValue={Status.Draft}>
              {[Status.Draft, Status.Live, Status.Deactivated].map((status) => {
                const isDisabled = checkStatusTransitionDisabled(status);

                return (
                  <div key={status} className="mr-3">
                    <FormControlLabel
                      value={status}
                      control={<Radio color="secondary" disabled={isDisabled} />}
                      style={{ fontSize: 13 }}
                      label={status}
                    />
                  </div>
                );
              })}
            </RadioGroup>
          </div>
        </div>

        <div className="row mb-2">
          <div className="col-12 col-xl-6 pl-0 d-flex justify-content-center">
            <Button
              variant="contained"
              disabled={isDeactivated}
              color="secondary"
              onClick={handleSave}
              style={{ minWidth: '250px', width: '50%' }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

Info.propTypes = {
  match: PropTypes.shape(Object).isRequired,
};

export default Info;
